// Simulación de un partido de copa: eventos minuto a minuto, goleadores y penales.

/** Nombre de un integrante, venga como string o como objeto de la base. */
const nombreDe = (x) => (typeof x === 'string' ? x : x?.nombre) || '—';

/**
 * Peso de cada línea para convertir. Un delantero la mete mucho más seguido que un central, y el
 * arquero casi nunca — queda en 1 y no en 0 para que exista el gol de arquero, que es justo el
 * que la gente recuerda.
 */
const PESO_GOL = { ataque: 60, medio: 28, defensa: 11, gk: 1 };

/** Amonestados: los que reparten son los de atrás y el mediocampo, no los delanteros. */
const PESO_TARJETA = { defensa: 42, medio: 38, ataque: 16, gk: 4 };

function elegirPorPeso(plantel, pesos, azar) {
    const bolillero = [];
    for (const [linea, peso] of Object.entries(pesos)) {
        for (const j of plantel[linea] || []) {
            for (let i = 0; i < peso; i++) bolillero.push(nombreDe(j));
        }
    }
    return bolillero.length ? bolillero[Math.floor(azar() * bolillero.length)] : null;
}

const elegirGoleador = (plantel, azar) => elegirPorPeso(plantel, PESO_GOL, azar);

/**
 * Un partido no es "tira un número y listo": se generan los goles repartidos en los 90 minutos,
 * con quién los hizo, para poder reproducirlo como transmisión. Si termina empatado hay alargue
 * y, si sigue igual, penales pateados de a uno.
 */

const MINUTOS = 90;
const MINUTOS_ALARGUE = 30;

/** Goles esperados de un equipo según su fuerza contra la del rival. */
function golesEsperados(propia, rival) {
    const ratio = propia / Math.max(1, propia + rival);
    // 1.3 goles de media en un partido parejo; se estira a ~2.6 para una diferencia grande.
    return Math.max(0.25, 2.6 * ratio);
}

/** Poisson por el método de Knuth: cuántos goles convierte en el partido. */
function poisson(lambda, azar) {
    const L = Math.exp(-lambda);
    let k = 0, p = 1;
    do { k++; p *= azar(); } while (p > L);
    return k - 1;
}

// Uno de cada ocho goles se anula por VAR. Aparece igual en la transmision —el gol se grita y
// despues se cae— porque ese es justamente el momento que hace ruido.
const PROB_VAR = 0.12;
const MOTIVOS_VAR = ['offside milimétrico', 'falta en el inicio de la jugada', 'mano en el control'];

function generarGoles(equipo, cantidad, desde, hasta, esLocal, azar, expulsados = new Map()) {
    const goles = [];
    for (let i = 0; i < cantidad; i++) {
        const anulado = azar() < PROB_VAR;
        const minuto = desde + Math.floor(azar() * (hasta - desde));

        // El goleador no puede ser alguien que ya se fue expulsado. Se reintenta unas cuantas
        // veces y, si no aparece nadie, queda el que salga: es preferible un gol raro a un gol
        // firmado por un jugador que no esta en la cancha.
        let autor = null;
        for (let intento = 0; intento < 12; intento++) {
            autor = elegirGoleador(equipo.plantel, azar);
            const expulsadoEn = autor != null ? expulsados.get(autor) : undefined;
            if (expulsadoEn === undefined || minuto < expulsadoEn) break;
        }

        goles.push({
            tipo: anulado ? 'gol_anulado' : 'gol',
            minuto,
            equipo: esLocal ? 'local' : 'visitante',
            nombre: equipo.nombreCorto,
            autor: autor || '—',
            motivo: anulado ? MOTIVOS_VAR[Math.floor(azar() * MOTIVOS_VAR.length)] : null,
        });
    }
    return goles;
}

/**
 * Tarjetas del partido. Las amarillas son narrativas, pero la roja SI tiene efecto: a partir de
 * ese minuto el equipo juega con uno menos, y eso se traduce en una chance extra de gol para el
 * rival. Sin eso, expulsar a alguien seria puro adorno.
 */
function generarTarjetas(local, visitante, hasta, azar) {
    const eventos = [];
    /** nombre -> minuto en que se fue expulsado */
    const expulsados = new Map();

    for (const [lado, equipo] of [['local', local], ['visitante', visitante]]) {
        // Se sortean TODAS las tarjetas con su minuto —amarillas y la roja directa— y recien
        // despues se procesan en orden de minuto. Procesarlas en el orden en que se sortearon
        // producia incoherencias: la roja por doble amarilla caia a los 13' y la "primera"
        // amarilla a los 31', o un jugador ya expulsado recibia otra tarjeta despues.
        const candidatas = [];

        const cuantas = Math.floor(azar() * 4);      // 0 a 3 amarillas por equipo
        for (let i = 0; i < cuantas; i++) {
            const quien = elegirPorPeso(equipo.plantel, PESO_TARJETA, azar);
            if (quien) candidatas.push({ quien, minuto: 8 + Math.floor(azar() * (hasta - 10)), directa: false });
        }
        if (azar() < 0.07) {
            const quien = elegirPorPeso(equipo.plantel, PESO_TARJETA, azar);
            if (quien) candidatas.push({ quien, minuto: 20 + Math.floor(azar() * (hasta - 25)), directa: true });
        }

        candidatas.sort((a, b) => a.minuto - b.minuto);

        const amonestados = new Set();
        for (const { quien, minuto, directa } of candidatas) {
            // Un expulsado ya no esta en la cancha: no puede recibir nada mas.
            if (expulsados.has(quien)) continue;

            if (directa) {
                expulsados.set(quien, minuto);
                eventos.push({ tipo: 'roja', doble: false, minuto, equipo: lado, autor: quien });
                continue;
            }
            const segunda = amonestados.has(quien);
            amonestados.add(quien);
            if (segunda) expulsados.set(quien, minuto);
            eventos.push({ tipo: segunda ? 'roja' : 'amarilla', doble: segunda, minuto, equipo: lado, autor: quien });
        }
    }
    return { eventos, expulsados };
}

/**
 * Simula el partido completo.
 * Cada equipo llega como { nombreCorto, fuerza, plantel } donde plantel tiene gk/defensa/medio/ataque.
 */
export function simularPartido(local, visitante, azar = Math.random) {
    const lambdaL = golesEsperados(local.fuerza, visitante.fuerza);
    const lambdaV = golesEsperados(visitante.fuerza, local.fuerza);

    // Las tarjetas van PRIMERO: hace falta saber quien esta expulsado y desde que minuto para que
    // no convierta despues de irse.
    const { eventos: tarjetas, expulsados } = generarTarjetas(local, visitante, MINUTOS, azar);

    let eventos = [
        ...generarGoles(local, poisson(lambdaL, azar), 1, MINUTOS, true, azar, expulsados),
        ...generarGoles(visitante, poisson(lambdaV, azar), 1, MINUTOS, false, azar, expulsados),
        ...tarjetas,
    ];

    // Efecto de las expulsiones: el rival del expulsado tiene una chance extra de convertir
    // despues de ese minuto.
    for (const roja of eventos.filter(e => e.tipo === 'roja')) {
        const rival = roja.equipo === 'local' ? visitante : local;
        const esLocal = roja.equipo !== 'local';
        if (azar() < 0.45 && roja.minuto < MINUTOS - 5) {
            eventos.push(...generarGoles(rival, 1, roja.minuto + 1, MINUTOS, esLocal, azar, expulsados));
        }
    }

    let marcador = contar(eventos);
    let alargue = false;
    let penales = null;

    if (marcador[0] === marcador[1]) {
        alargue = true;
        const extra = [
            ...generarGoles(local, poisson(lambdaL * MINUTOS_ALARGUE / MINUTOS, azar), 91, 120, true, azar, expulsados),
            ...generarGoles(visitante, poisson(lambdaV * MINUTOS_ALARGUE / MINUTOS, azar), 91, 120, false, azar, expulsados),
        ];
        eventos = [...eventos, ...extra];
        marcador = contar(eventos);
        if (marcador[0] === marcador[1]) penales = tandaDePenales(local, visitante, azar, expulsados);
    }

    eventos.sort((a, b) => a.minuto - b.minuto);

    const ganaLocal = penales
        ? penales.ganaLocal
        : marcador[0] > marcador[1];

    return { eventos, marcador, alargue, penales, ganaLocal };
}

/** Solo cuentan los goles validos: los anulados por VAR quedan en la transmision pero no suman. */
function contar(eventos) {
    const validos = eventos.filter(e => e.tipo === 'gol');
    return [
        validos.filter(e => e.equipo === 'local').length,
        validos.filter(e => e.equipo === 'visitante').length,
    ];
}

/**
 * Tanda de penales: cinco por lado y, si siguen iguales, muerte súbita. Se resuelve entera acá
 * y la pantalla la reproduce pateada por pateada.
 */
function tandaDePenales(local, visitante, azar, expulsados = new Map()) {
    // Patean primero los de arriba: delanteros, después medios, y el arquero último.
    // Los expulsados no patean: se fueron antes de que terminara el partido.
    const pateadores = (eq) => [
        ...(eq.plantel.ataque || []), ...(eq.plantel.medio || []),
        ...(eq.plantel.defensa || []), ...(eq.plantel.gk || []),
    ].map(nombreDe).filter(n => !expulsados.has(n));
    const listaL = pateadores(local);
    const listaV = pateadores(visitante);
    // Una diferencia de fuerza grande mueve poco la aguja: en penales se emparejan.
    const probL = 0.62 + 0.14 * (local.fuerza / (local.fuerza + visitante.fuerza));
    const probV = 0.62 + 0.14 * (visitante.fuerza / (local.fuerza + visitante.fuerza));

    const tiros = [];
    let gL = 0, gV = 0;

    const patear = (equipo, indice, prob, lista) => {
        const convierte = azar() < prob;
        tiros.push({
            equipo, ronda: Math.floor(indice / 1) + 1,
            pateador: lista[indice % lista.length] || '—',
            convierte,
        });
        return convierte;
    };

    for (let i = 0; i < 5; i++) {
        if (patear('local', i, probL, listaL)) gL++;
        if (definido(gL, gV, i + 1, 5)) break;
        if (patear('visitante', i, probV, listaV)) gV++;
        if (definido(gL, gV, i + 1, 5)) break;
    }

    // Muerte súbita
    let i = 5;
    while (gL === gV && i < 20) {
        const a = patear('local', i, probL, listaL); if (a) gL++;
        const b = patear('visitante', i, probV, listaV); if (b) gV++;
        i++;
    }

    return { tiros, marcador: [gL, gV], ganaLocal: gL > gV };
}

/** ¿La tanda ya está definida aunque falten tiros? Evita patear penales que no cambian nada. */
function definido(gL, gV, tiradas, total) {
    const restanL = total - tiradas;
    const restanV = total - tiradas;
    return gL > gV + restanV || gV > gL + restanL;
}
